import {
  PRODUCT_PHOTO_HEIGHT,
  PRODUCT_PHOTO_WIDTH
} from 'components/structure-page/2d/product-photo';
import { GridSpaceDataType, PositionType } from 'data/types';
import { isPointInPolygon, polygonBoundingBox } from 'utils/footprint';
import { estimateSpaceLabelSize } from 'utils/space-label-size';
import {
  isLabelBoxInsidePolygon,
  labelBoxOverlapsObstacle,
  polygonVisualCenter,
  SPACE_LABEL_OBSTACLE_CLEARANCE,
  SpaceLabelObstacle,
  SpaceLabelSize,
  spaceRectVertices
} from 'utils/space-label-position';

function pointInSpaceBounds(
  x: number,
  y: number,
  space: GridSpaceDataType
): boolean {
  const { position } = space;
  return (
    x >= position.x &&
    x <= position.x + position.width &&
    y >= position.y &&
    y <= position.y + position.height
  );
}

export function smallestContainingSpace(
  x: number,
  y: number,
  spaces: GridSpaceDataType[]
): GridSpaceDataType | null {
  let best: GridSpaceDataType | null = null;
  let bestArea = Number.POSITIVE_INFINITY;
  spaces.forEach((space) => {
    if (!pointInSpaceBounds(x, y, space)) return;
    const area = space.position.width * space.position.height;
    if (area < bestArea) {
      best = space;
      bestArea = area;
    }
  });
  return best;
}

function spaceVertices(space: GridSpaceDataType): PositionType[] {
  if (space.vertices && space.vertices.length >= 3) {
    return space.vertices;
  }
  return spaceRectVertices(space.position.width, space.position.height);
}

function packCenteredStack(args: {
  spaceStart: number;
  spaceSize: number;
  firstSize: number;
  secondSize: number;
  gap: number;
}): { first: number; second: number } {
  const { spaceStart, spaceSize, firstSize, secondSize, gap } = args;
  const needed = firstSize + gap + secondSize;
  const resolvedGap =
    needed <= spaceSize ? gap : Math.max(0, spaceSize - firstSize - secondSize);
  const stack = firstSize + resolvedGap + secondSize;
  const stackStart = spaceStart + (spaceSize - stack) / 2;
  return {
    first: stackStart + firstSize / 2,
    second: stackStart + firstSize + resolvedGap + secondSize / 2
  };
}

export function packSpaceLabelAndPhoto(args: {
  vertices: PositionType[];
  labelSize: SpaceLabelSize;
  photo: SpaceLabelObstacle;
}): { labelAnchor: PositionType; photoCenter: PositionType } | null {
  const preferred = polygonVisualCenter(args.vertices);
  if (!preferred) return null;

  const photoCenterPref = {
    x: args.photo.x + args.photo.width / 2,
    y: args.photo.y + args.photo.height / 2
  };

  if (
    isLabelBoxInsidePolygon(preferred, args.labelSize, args.vertices) &&
    !labelBoxOverlapsObstacle(preferred, args.labelSize, [args.photo])
  ) {
    return { labelAnchor: preferred, photoCenter: photoCenterPref };
  }

  const bb = polygonBoundingBox(args.vertices);
  const gap = SPACE_LABEL_OBSTACLE_CLEARANCE;
  const labelAbove = preferred.y <= photoCenterPref.y;

  if (labelAbove) {
    const packedY = packCenteredStack({
      spaceStart: bb.minY,
      spaceSize: bb.maxY - bb.minY,
      firstSize: args.labelSize.height,
      secondSize: args.photo.height,
      gap
    });
    const labelAnchor = { x: preferred.x, y: packedY.first };
    const photoCenter = { x: photoCenterPref.x, y: packedY.second };
    if (
      isPointInPolygon(labelAnchor, args.vertices) &&
      isPointInPolygon(photoCenter, args.vertices)
    ) {
      return { labelAnchor, photoCenter };
    }
  } else {
    const packedY = packCenteredStack({
      spaceStart: bb.minY,
      spaceSize: bb.maxY - bb.minY,
      firstSize: args.photo.height,
      secondSize: args.labelSize.height,
      gap
    });
    const photoCenter = { x: photoCenterPref.x, y: packedY.first };
    const labelAnchor = { x: preferred.x, y: packedY.second };
    if (
      isPointInPolygon(labelAnchor, args.vertices) &&
      isPointInPolygon(photoCenter, args.vertices)
    ) {
      return { labelAnchor, photoCenter };
    }
  }

  const labelLeft = preferred.x <= photoCenterPref.x;
  if (labelLeft) {
    const packedX = packCenteredStack({
      spaceStart: bb.minX,
      spaceSize: bb.maxX - bb.minX,
      firstSize: args.labelSize.width,
      secondSize: args.photo.width,
      gap
    });
    const labelAnchor = { x: packedX.first, y: preferred.y };
    const photoCenter = { x: packedX.second, y: photoCenterPref.y };
    if (
      isPointInPolygon(labelAnchor, args.vertices) &&
      isPointInPolygon(photoCenter, args.vertices)
    ) {
      return { labelAnchor, photoCenter };
    }
  } else {
    const packedX = packCenteredStack({
      spaceStart: bb.minX,
      spaceSize: bb.maxX - bb.minX,
      firstSize: args.photo.width,
      secondSize: args.labelSize.width,
      gap
    });
    const photoCenter = { x: packedX.first, y: photoCenterPref.y };
    const labelAnchor = { x: packedX.second, y: preferred.y };
    if (
      isPointInPolygon(labelAnchor, args.vertices) &&
      isPointInPolygon(photoCenter, args.vertices)
    ) {
      return { labelAnchor, photoCenter };
    }
  }

  return null;
}

export function resolvePhotoCenterInSpaces(
  preferred: PositionType,
  spaces: GridSpaceDataType[]
): PositionType {
  const space = smallestContainingSpace(preferred.x, preferred.y, spaces);
  if (!space) return preferred;

  const vertices = spaceVertices(space);
  const labelSize = estimateSpaceLabelSize(space.label, {
    maxWidth: space.position.width * 0.9
  });
  const packed = packSpaceLabelAndPhoto({
    vertices,
    labelSize,
    photo: {
      x: preferred.x - PRODUCT_PHOTO_WIDTH / 2 - space.position.x,
      y: preferred.y - PRODUCT_PHOTO_HEIGHT / 2 - space.position.y,
      width: PRODUCT_PHOTO_WIDTH,
      height: PRODUCT_PHOTO_HEIGHT
    }
  });
  if (!packed) return preferred;
  return {
    x: space.position.x + packed.photoCenter.x,
    y: space.position.y + packed.photoCenter.y
  };
}
